import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  fetchBillingInfo, createCheckoutSession, checkPaymentStatus,
  cancelSubscription, reactivateSubscription, createPortalSession,
  fetchStripeInvoices,
} from "@/lib/api";
import { getErrorMessage } from "@/lib/utils";
import {
  CreditCard, CheckCircle, Clock, WarningCircle, ArrowSquareOut,
  Receipt, XCircle, ArrowClockwise, Download, Eye, Spinner,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  trial: { label: "Free Trial", color: "text-foreground bg-foreground/5", icon: Clock },
  active: { label: "Active", color: "text-emerald-600 bg-emerald-50", icon: CheckCircle },
  past_due: { label: "Past Due", color: "text-amber-600 bg-amber-50", icon: WarningCircle },
  cancelled: { label: "Cancelled", color: "text-red-600 bg-red-50", icon: XCircle },
  expired: { label: "Expired", color: "text-slate-600 bg-slate-100", icon: WarningCircle },
};

const INVOICE_STATUS_COLORS = {
  paid: "text-emerald-700 bg-emerald-50",
  open: "text-amber-700 bg-amber-50",
  draft: "text-slate-600 bg-slate-100",
  void: "text-slate-400 bg-slate-50",
  uncollectible: "text-red-600 bg-red-50",
};

function formatCurrency(cents, currency = "aud") {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function formatDate(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-AU", {
    day: "numeric", month: "short", year: "numeric",
  });
}

export default function BillingPage() {
  const [searchParams] = useSearchParams();
  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const load = useCallback(async () => {
    try {
      const info = await fetchBillingInfo();
      setBilling(info);
    } catch { toast.error("Failed to load billing info"); }
    finally { setLoading(false); }
  }, []);

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const data = await fetchStripeInvoices();
      setInvoices(data);
    } catch { /* silently fail — no invoices */ }
    finally { setInvoicesLoading(false); }
  }, []);

  useEffect(() => { load(); loadInvoices(); }, [load, loadInvoices]);

  // Check payment status if returning from Stripe
  useEffect(() => {
    const status = searchParams.get("status");
    const sessionId = searchParams.get("session_id");
    if (status === "success" && sessionId) {
      checkPaymentStatus(sessionId).then(res => {
        if (res.status === "active") {
          toast.success("Payment successful! Subscription activated.");
          load();
          loadInvoices();
        }
      }).catch(() => {});
    } else if (status === "cancelled") {
      toast.error("Payment cancelled.");
    }
  }, [searchParams, load, loadInvoices]);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const res = await createCheckoutSession({ origin_url: window.location.origin });
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to start checkout"));
    } finally { setCheckoutLoading(false); }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      const res = await cancelSubscription();
      toast.success(res.message || "Subscription cancellation scheduled.");
      setShowCancelDialog(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to cancel subscription"));
    } finally { setCancelLoading(false); }
  };

  const handleReactivate = async () => {
    setReactivateLoading(true);
    try {
      const res = await reactivateSubscription();
      toast.success(res.message || "Subscription reactivated!");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to reactivate subscription"));
    } finally { setReactivateLoading(false); }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const res = await createPortalSession({ return_url: window.location.href });
      if (res.portal_url) {
        window.location.href = res.portal_url;
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to open payment portal"));
    } finally { setPortalLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400">Loading...</div>;
  if (!billing?.has_org) return <div className="text-center py-20 text-slate-400">Create an organization first</div>;

  const statusCfg = STATUS_CONFIG[billing.subscription_status] || STATUS_CONFIG.trial;
  const StatusIcon = statusCfg.icon;
  const isActive = billing.subscription_status === "active";
  const isTrial = billing.subscription_status === "trial";
  const isPastDue = billing.subscription_status === "past_due";
  const isCancelled = billing.subscription_status === "cancelled" || billing.subscription_status === "expired";
  const hasSubscription = !!billing.stripe_subscription_id;
  const cancelScheduled = billing.cancel_at_period_end;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="billing-page">
      <div>
        <h1 className="text-xl font-bold font-heading text-slate-900">Billing & Subscription</h1>
        <p className="text-sm text-slate-500 mt-0.5">{billing.org_name}</p>
      </div>

      {/* ======== Status Card ======== */}
      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="billing-status-card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusCfg.color}`}>
              <StatusIcon weight="bold" className="w-5 h-5" />
            </div>
            <div>
              <div className="text-lg font-bold text-slate-900">
                {cancelScheduled ? "Cancelling" : statusCfg.label}
              </div>
              <div className="text-xs text-slate-500" data-testid="days-left">
                {cancelScheduled
                  ? `Access until ${billing.subscription_expires_at?.slice(0, 10) || "—"}`
                  : `${billing.days_left} days remaining`}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-black font-mono text-foreground">${billing.price_aud}</div>
            <div className="text-xs text-slate-500">AUD / month</div>
          </div>
        </div>

        {/* ---- Trial CTA ---- */}
        {isTrial && (
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center gap-3 p-3 bg-foreground/5 rounded-lg mb-4">
              <Clock weight="bold" className="w-5 h-5 text-foreground flex-shrink-0" />
              <div>
                <div className="text-sm font-bold text-foreground">Free Trial</div>
                <div className="text-xs text-foreground/70">Your trial expires in {billing.days_left} days. Subscribe now to avoid interruption.</div>
              </div>
            </div>
            <button data-testid="subscribe-btn" onClick={handleCheckout} disabled={checkoutLoading}
              className="w-full h-12 bg-primary text-primary-foreground rounded-md font-bold text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
              <CreditCard weight="bold" className="w-5 h-5" />
              {checkoutLoading ? "Redirecting to Stripe..." : `Subscribe — $${billing.price_aud}/month AUD`}
            </button>
          </div>
        )}

        {/* ---- Active — normal ---- */}
        {isActive && !cancelScheduled && (
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
              <CheckCircle weight="bold" className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div>
                <div className="text-sm font-bold text-emerald-900">Active Subscription</div>
                <div className="text-xs text-emerald-700">Next billing on {billing.subscription_expires_at?.slice(0, 10) || "—"}</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              {billing.stripe_customer_id && (
                <button data-testid="manage-payment-btn" onClick={handlePortal} disabled={portalLoading}
                  className="flex-1 h-10 bg-slate-100 text-slate-800 rounded-md font-semibold text-sm hover:bg-slate-200 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                  <ArrowSquareOut weight="bold" className="w-4 h-4" />
                  {portalLoading ? "Opening..." : "Manage Payment Method"}
                </button>
              )}
              <button data-testid="cancel-btn" onClick={() => setShowCancelDialog(true)}
                className="h-10 px-4 text-red-600 rounded-md font-semibold text-sm hover:bg-red-50 flex items-center justify-center gap-2 transition-colors">
                <XCircle weight="bold" className="w-4 h-4" />
                Cancel Subscription
              </button>
            </div>
          </div>
        )}

        {/* ---- Active but scheduled for cancellation ---- */}
        {isActive && cancelScheduled && (
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
              <WarningCircle weight="bold" className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <div className="text-sm font-bold text-amber-900">Cancellation Scheduled</div>
                <div className="text-xs text-amber-700">
                  Your subscription will end on {billing.subscription_expires_at?.slice(0, 10) || "—"}. You'll keep full access until then.
                </div>
              </div>
            </div>
            <button data-testid="reactivate-btn" onClick={handleReactivate} disabled={reactivateLoading}
              className="w-full h-10 bg-emerald-600 text-white rounded-md font-bold text-sm hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
              <ArrowClockwise weight="bold" className="w-4 h-4" />
              {reactivateLoading ? "Reactivating..." : "Keep My Subscription"}
            </button>
          </div>
        )}

        {/* ---- Past Due ---- */}
        {isPastDue && (
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
              <WarningCircle weight="bold" className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <div className="text-sm font-bold text-amber-900">Payment Failed</div>
                <div className="text-xs text-amber-700">We couldn't process your payment. Please update your payment method to avoid service interruption.</div>
              </div>
            </div>
            {billing.stripe_customer_id && (
              <button data-testid="update-payment-btn" onClick={handlePortal} disabled={portalLoading}
                className="w-full h-10 bg-amber-600 text-white rounded-md font-bold text-sm hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                <CreditCard weight="bold" className="w-4 h-4" />
                {portalLoading ? "Opening..." : "Update Payment Method"}
              </button>
            )}
          </div>
        )}

        {/* ---- Cancelled / Expired ---- */}
        {isCancelled && (
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg mb-4">
              <XCircle weight="bold" className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div>
                <div className="text-sm font-bold text-red-900">Subscription Inactive</div>
                <div className="text-xs text-red-700">Your access is limited. Subscribe to restore full access.</div>
              </div>
            </div>
            <button data-testid="resubscribe-btn" onClick={handleCheckout} disabled={checkoutLoading}
              className="w-full h-12 bg-primary text-primary-foreground rounded-md font-bold text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
              <CreditCard weight="bold" className="w-5 h-5" />
              {checkoutLoading ? "Redirecting..." : `Resubscribe — $${billing.price_aud}/month AUD`}
            </button>
          </div>
        )}
      </div>

      {/* ======== Cancel Confirmation Dialog ======== */}
      {showCancelDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="cancel-dialog">
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <WarningCircle weight="bold" className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Cancel Subscription?</h3>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-1">
              Your subscription will remain active until the end of the current billing period
              ({billing.subscription_expires_at?.slice(0, 10) || "—"}).
            </p>
            <p className="text-sm text-slate-600 mb-6">
              After that, your team will lose access to the app. You can resubscribe at any time.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowCancelDialog(false)}
                className="flex-1 h-10 bg-slate-100 text-slate-800 rounded-md font-semibold text-sm hover:bg-slate-200 transition-colors">
                Keep Subscription
              </button>
              <button data-testid="confirm-cancel-btn" onClick={handleCancel} disabled={cancelLoading}
                className="flex-1 h-10 bg-red-600 text-white rounded-md font-semibold text-sm hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                {cancelLoading ? "Cancelling..." : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======== Invoice History ======== */}
      {(hasSubscription || invoices.length > 0) && (
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="invoice-history">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <Receipt weight="bold" className="w-4 h-4" />
              Invoice History
            </h3>
            {billing.stripe_customer_id && (
              <button onClick={handlePortal} disabled={portalLoading}
                className="text-xs text-slate-500 hover:text-foreground font-medium flex items-center gap-1 transition-colors">
                <ArrowSquareOut weight="bold" className="w-3.5 h-3.5" />
                View all in Stripe
              </button>
            )}
          </div>

          {invoicesLoading ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <Spinner weight="bold" className="w-5 h-5 animate-spin mr-2" />
              Loading invoices...
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-6">No invoices yet</div>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 whitespace-nowrap">
                    <th className="pb-2 px-2">Invoice</th>
                    <th className="pb-2 px-2">Date</th>
                    <th className="pb-2 px-2">Amount</th>
                    <th className="pb-2 px-2">Status</th>
                    <th className="pb-2 px-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-slate-50/50 transition-colors whitespace-nowrap">
                      <td className="py-3 px-2">
                        <span className="font-mono text-xs text-slate-700">{inv.number || inv.id.slice(-8)}</span>
                      </td>
                      <td className="py-3 px-2 text-slate-600">{formatDate(inv.created)}</td>
                      <td className="py-3 px-2 font-semibold text-slate-900">
                        {formatCurrency(inv.amount_paid || inv.amount_due, inv.currency)}
                      </td>
                      <td className="py-3 px-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold capitalize ${INVOICE_STATUS_COLORS[inv.status] || "text-slate-600 bg-slate-100"}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {inv.hosted_invoice_url && (
                            <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                              className="p-1.5 rounded-md text-slate-400 hover:text-foreground hover:bg-slate-100 transition-colors"
                              title="View invoice">
                              <Eye weight="bold" className="w-4 h-4" />
                            </a>
                          )}
                          {inv.invoice_pdf && (
                            <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                              className="p-1.5 rounded-md text-slate-400 hover:text-foreground hover:bg-slate-100 transition-colors"
                              title="Download PDF">
                              <Download weight="bold" className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ======== Plan Details ======== */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">What's Included</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {["Unlimited vehicles", "Unlimited rentals", "Invoice generation & email", "PDF agreements & signatures", "Team collaboration", "Dashboard analytics", "Customer management", "Inspection workflows"].map(f => (
            <div key={f} className="flex items-center gap-2 text-sm text-slate-700">
              <CheckCircle weight="bold" className="w-4 h-4 text-emerald-500 flex-shrink-0" /> {f}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
